import React, { useState, useEffect, useRef } from 'react';
import Map from 'ol/Map';
import View from 'ol/View';
import GeoJSON from 'ol/format/GeoJSON';
import VectorLayer from 'ol/layer/Vector';
import VectorSource from 'ol/source/Vector';
import Overlay from 'ol/Overlay';
import { MouseWheelZoom, defaults as defaultInteractions } from 'ol/interaction';
import { Style, Fill, Stroke } from 'ol/style';
import { isEmpty as extentIsEmpty } from 'ol/extent';

const calcularRangosDinamicos = (entidadesPorPartido) => {
    const valores = entidadesPorPartido
        .map(p => parseInt(p.cantidad || 0))
        .filter(v => v > 0)
        .sort((a, b) => a - b);

    if (valores.length === 0) {
        return [0, 0, 0, 0, 0];
    }

    const max = valores[valores.length - 1] || 100;

    if (new Set(valores).size === 1) {
        const q = Math.max(1, Math.ceil(max / 5));
        return [q, q * 2, q * 3, q * 4, max];
    }

    const q1 = valores[Math.floor(valores.length * 0.2)] || 1;
    const q2 = valores[Math.floor(valores.length * 0.4)] || 2;
    const q3 = valores[Math.floor(valores.length * 0.6)] || 5;
    const q4 = valores[Math.floor(valores.length * 0.8)] || 15;

    return [q1, q2, q3, q4, max];
};

export const ComponenteMapas = ({ entidadesPorPartido = [], partidoSeleccionadoId = null }) => {
    const mapaProvinciaRef = useRef(null);
    const mapaConurbanoRef = useRef(null);

    const [permitirZoom, setPermitirZoom] = useState(false);

    const mouseWheelProvincia = useRef(new MouseWheelZoom());
    const mouseWheelConurbano = useRef(new MouseWheelZoom());

    const mapaProvinciaInstancia = useRef(null);
    const mapaConurbanoInstancia = useRef(null);

    const rangos = React.useMemo(() => {
        return calcularRangosDinamicos(entidadesPorPartido);
    }, [entidadesPorPartido]);

    const estructuraDatos = React.useMemo(() => {
        const mapa = new Map();
        entidadesPorPartido.forEach(p => {
            if (p.partido_id) {
                mapa.set(parseInt(p.partido_id), parseInt(p.cantidad || 0));
            }
        });
        return mapa;
    }, [entidadesPorPartido]);

    // 📌 REFS: los mapas se crean UNA sola vez; estos refs se actualizan con los datos vivos
    // para que los estilos y el tooltip lean SIEMPRE los valores actuales sin destruir el mapa.
    const estructuraDatosRef = useRef(estructuraDatos);
    const partidoSeleccionadoIdRef = useRef(partidoSeleccionadoId);
    const rangosRef = useRef(rangos);
    const capaProvinciaRef = useRef(null);
    const capaConurbanoRef = useRef(null);

    const obtenerEstiloPoligono = (feature) => {
        const idPartidoGeo = parseInt(feature.get('partido_nro'));
        const cantidad = estructuraDatosRef.current.get(idPartidoGeo) || 0;

        let colorRelleno = '#e2e8f0';
        if (cantidad > 0) {
            if (cantidad <= rangosRef.current[0]) colorRelleno = '#bae6fd';
            else if (cantidad <= rangosRef.current[1]) colorRelleno = '#7dd3fc';
            else if (cantidad <= rangosRef.current[2]) colorRelleno = '#38bdf8';
            else if (cantidad <= rangosRef.current[3]) colorRelleno = '#0284c7';
            else colorRelleno = '#0369a1';
        }

        const partidoSeleccionadoIdActual = partidoSeleccionadoIdRef.current;
        const rawFiltro = Array.isArray(partidoSeleccionadoIdActual)
            ? partidoSeleccionadoIdActual
            : [partidoSeleccionadoIdActual];

        const esElSeleccionado = partidoSeleccionadoIdActual &&
            !rawFiltro.includes('Todos') &&
            rawFiltro.some(p => parseInt(p) === idPartidoGeo);

        if (esElSeleccionado) {
            return new Style({
                fill: new Fill({ color: colorRelleno }),
                stroke: new Stroke({
                    color: '#ef4444',
                    width: 3.5,
                }),
                zIndex: 999
            });
        }

        return new Style({
            fill: new Fill({ color: colorRelleno }),
            stroke: new Stroke({ color: '#ffffff', width: 0.8 })
        });
    };

    // 📌 Mantenemos los refs sincronizados con los datos vivos (para estilos y tooltip)
    React.useEffect(() => { estructuraDatosRef.current = estructuraDatos; }, [estructuraDatos]);
    React.useEffect(() => { partidoSeleccionadoIdRef.current = partidoSeleccionadoId; }, [partidoSeleccionadoId]);
    React.useEffect(() => { rangosRef.current = rangos; }, [rangos]);

    // 🎨 Cuando cambian los datos, forzamos el redibujo de las capas (sin recrear el mapa ni el tooltip)
    React.useEffect(() => {
        capaProvinciaRef.current?.changed();
        capaConurbanoRef.current?.changed();
    }, [estructuraDatos, partidoSeleccionadoId, rangos]);

    useEffect(() => {
        if (!mapaProvinciaRef.current || !mapaConurbanoRef.current) return;

        const elProvincia = document.getElementById('tooltip-provincia');
        const elConurbano = document.getElementById('tooltip-conurbano');

        const overlayProvincia = new Overlay({
            element: elProvincia,
            offset: [10, 10],
            positioning: 'top-left'
        });

        const overlayConurbano = new Overlay({
            element: elConurbano,
            offset: [10, 10],
            positioning: 'top-left'
        });

        const fuenteProvincia = new VectorSource({
            url: '/deptos_ARBA.geojson',
            format: new GeoJSON()
        });

        const capaProvincia = new VectorLayer({ source: fuenteProvincia, style: obtenerEstiloPoligono });
        capaProvinciaRef.current = capaProvincia;

        const mapaProvincia = new Map({
            target: mapaProvinciaRef.current,
            layers: [capaProvincia],
            controls: [],
            interactions: defaultInteractions({ mouseWheelZoom: false }),
            view: new View({ center: [-6700000, -4450000], zoom: 6, minZoom: 5, maxZoom: 9 })
        });
        mapaProvincia.addOverlay(overlayProvincia);
        mapaProvinciaInstancia.current = mapaProvincia;

        const fuenteConurbano = new VectorSource({
            url: '/partidos_RMBA_N.geojson',
            format: new GeoJSON()
        });

        const capaConurbano = new VectorLayer({ source: fuenteConurbano, style: obtenerEstiloPoligono });
        capaConurbanoRef.current = capaConurbano;

        const mapaConurbano = new Map({
            target: mapaConurbanoRef.current,
            layers: [capaConurbano],
            controls: [],
            interactions: defaultInteractions({ mouseWheelZoom: false }),
            view: new View({ center: [-6500000, -4100000], zoom: 9, minZoom: 8, maxZoom: 12 })
        });
        mapaConurbano.addOverlay(overlayConurbano);
        mapaConurbanoInstancia.current = mapaConurbano;

        fuenteProvincia.once('change', () => {
            if (fuenteProvincia.getState() === 'ready') {
                const extent = fuenteProvincia.getExtent();
                if (!extentIsEmpty(extent)) {
                    mapaProvincia.getView().fit(extent, { padding: [20, 20, 20, 20] });
                }
            }
        });
        fuenteConurbano.once('change', () => {
            if (fuenteConurbano.getState() === 'ready') {
                const extent = fuenteConurbano.getExtent();
                if (!extentIsEmpty(extent)) {
                    mapaConurbano.getView().fit(extent, { padding: [10, 10, 10, 10] });
                }
            }
        });

        const manejarMoverPuntero = (evt, mapaActivo, overlayActivo, elementoHtml) => {
            if (!elementoHtml) return;

            const feature = mapaActivo.forEachFeatureAtPixel(evt.pixel, (feat) => feat);

            if (feature) {
                const idPartidoGeo = parseInt(feature.get('partido_nro'));
                const nombrePartido = feature.get('nam') || 'Desconocido';
                const cantidad = estructuraDatosRef.current.get(idPartidoGeo) || 0;

                elementoHtml.innerHTML = `
            <div class="font-bold border-b border-gray-700 pb-1 mb-1 text-gray-200">${nombrePartido}</div>
            <div class="text-emerald-400 font-semibold text-right">${cantidad} ${cantidad === 1 ? 'Entidad' : 'Entidades'}</div>
        `;

                elementoHtml.style.opacity = '1';
                overlayActivo.setPosition(evt.coordinate);
                mapaActivo.getTargetElement().style.cursor = 'pointer';
            } else {
                elementoHtml.style.opacity = '0';
                overlayActivo.setPosition(undefined);
                mapaActivo.getTargetElement().style.cursor = 'grab';
            }
        };

        mapaProvincia.on('pointermove', (e) => manejarMoverPuntero(e, mapaProvincia, overlayProvincia, elProvincia));
        mapaConurbano.on('pointermove', (e) => manejarMoverPuntero(e, mapaConurbano, overlayConurbano, elConurbano));

        return () => {
            mapaProvincia.setTarget(null);
            mapaConurbano.setTarget(null);
            capaProvinciaRef.current = null;
            capaConurbanoRef.current = null;
        };
    }, []);

    useEffect(() => {
        const mapaProv = mapaProvinciaInstancia.current;
        const mapaConur = mapaConurbanoInstancia.current;

        if (!mapaProv || !mapaConur) return;

        if (permitirZoom) {
            mapaProv.addInteraction(mouseWheelProvincia.current);
            mapaConur.addInteraction(mouseWheelConurbano.current);
        } else {
            mapaProv.removeInteraction(mouseWheelProvincia.current);
            mapaConur.removeInteraction(mouseWheelConurbano.current);
        }
    }, [permitirZoom]);

    return (
        <div className="w-full flex flex-col mb-6">

            <div className="w-full flex flex-col xl:flex-row gap-6">
                <div className="xl:flex-[2] bg-white p-5 rounded-xl border border-gray-200 shadow-sm flex flex-col h-[550px] dark:bg-gray-800 dark:border-gray-600">
                    <div className="mb-3 flex justify-between items-center">
                        <h3 className="text-base font-bold text-gray-800 tracking-tight uppercase">Distribución Provincial de Entidades</h3>
                        <div className="flex items-center gap-3">
                            <label className="flex items-center space-x-2 text-xs text-gray-600 cursor-pointer bg-white hover:bg-gray-50 px-3 py-2 rounded-xl border border-gray-200 shadow-sm transition-colors select-none dark:bg-gray-800 dark:text-gray-300 dark:border-gray-600">
                                <input
                                    type="checkbox"
                                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 cursor-pointer w-4 h-4"
                                    checked={permitirZoom}
                                    onChange={(e) => setPermitirZoom(e.target.checked)}
                                />
                                <span className="font-medium text-gray-700 dark:text-gray-300">Habilitar zoom</span>
                            </label>
                            <div className="flex flex-wrap items-center gap-3 text-xs text-gray-600 bg-gray-50/80 p-2.5 rounded-xl border border-gray-100 backdrop-blur-sm shadow-inner dark:bg-gray-700/50 dark:text-gray-300 dark:border-gray-600">
                            <div className="flex items-center space-x-1.5">
                                <span className="w-3.5 h-3.5 bg-[#e2e8f0] inline-block rounded border border-gray-300 shadow-sm"></span>
                                <span className="font-medium">0</span>
                            </div>
                            <div className="flex items-center space-x-1.5">
                                <span className="w-3.5 h-3.5 bg-[#bae6fd] inline-block rounded shadow-sm"></span>
                                <span className="font-medium">1 - {rangos[0]}</span>
                            </div>
                            <div className="flex items-center space-x-1.5">
                                <span className="w-3.5 h-3.5 bg-[#7dd3fc] inline-block rounded shadow-sm"></span>
                                <span className="font-medium">{rangos[0] + 1} - {rangos[1]}</span>
                            </div>
                            <div className="flex items-center space-x-1.5">
                                <span className="w-3.5 h-3.5 bg-[#38bdf8] inline-block rounded shadow-sm"></span>
                                <span className="font-medium">{rangos[1] + 1} - {rangos[2]}</span>
                            </div>
                            <div className="flex items-center space-x-1.5">
                                <span className="w-3.5 h-3.5 bg-[#0284c7] inline-block rounded shadow-sm"></span>
                                <span className="font-medium">{rangos[2] + 1} - {rangos[3]}</span>
                            </div>
                            <div className="flex items-center space-x-1.5">
                                <span className="w-3.5 h-3.5 bg-[#0369a1] inline-block rounded shadow-sm"></span>
                                <span className="font-medium">{rangos[3] + 1} +</span>
                            </div>
                        </div>
                        </div>
                    </div>
                    <div ref={mapaProvinciaRef} className="flex-1 w-full rounded-lg bg-slate-50 overflow-hidden cursor-grab active:cursor-grabbing dark:bg-gray-900" />
                    <div id="tooltip-provincia" className="absolute z-50 bg-gray-900/95 text-white text-xs px-3 py-2 rounded-lg shadow-md pointer-events-none transition-opacity duration-150 opacity-0 font-sans border border-gray-700 min-w-[120px]" />
                </div>

                <div className="xl:flex-[1] bg-white p-5 rounded-xl border border-gray-200 shadow-sm flex flex-col h-[550px] dark:bg-gray-800 dark:border-gray-600">
                    <div className="mb-3">
                        <h3 className="text-base font-bold text-gray-800 tracking-tight uppercase">Región Metropolitana (RMBA)</h3>
                    </div>
                    <div ref={mapaConurbanoRef} className="flex-1 w-full rounded-lg bg-slate-50 overflow-hidden cursor-grab active:cursor-grabbing dark:bg-gray-900" />
                    <div id="tooltip-conurbano" className="absolute z-50 bg-gray-900/95 text-white text-xs px-3 py-2 rounded-lg shadow-md pointer-events-none transition-opacity duration-150 opacity-0 font-sans border border-gray-700 min-w-[120px]" />
                </div>
            </div>

        </div>
    );
};
